import { useCallback, useEffect, useState } from "react";
import { Activity, ArrowRight, CalendarDays, HeartPulse, Languages, MapPin, School, ShoppingBasket, Sparkles, Stethoscope, Users } from "lucide-react";
import { Registration } from "./components/Registration";
import { StaffPortal } from "./components/StaffPortal";
import { copy, type Language } from "./data/content";
import { appText, languageNames, normalizeLanguage } from "./data/i18n";
import { finishLogin, getSession, type Session } from "./lib/auth";
import type { RuntimeConfig } from "./lib/config";

const languageStorageKey = "mega-health-fair-language";

function readStoredLanguage() {
  try { return normalizeLanguage(window.localStorage.getItem(languageStorageKey)); }
  catch { return null; }
}

function storeLanguage(language: Language) {
  try { window.localStorage.setItem(languageStorageKey, language); }
  catch { /* Language still works for this page when browser storage is unavailable. */ }
}

export function App({ config }: { config: RuntimeConfig }) {
  const queryLanguage = normalizeLanguage(new URLSearchParams(window.location.search).get("lang"));
  const storedLanguage = readStoredLanguage();
  const browserLanguage = normalizeLanguage(window.navigator.language);
  const [language, setLanguage] = useState<Language>(queryLanguage || storedLanguage || browserLanguage || "EN");
  const [showLanguageWelcome, setShowLanguageWelcome] = useState(!queryLanguage && !storedLanguage && window.location.pathname !== "/staff" && window.location.pathname !== "/callback");
  const [session, setSession] = useState<Session | null>(() => getSession());
  const [authError, setAuthError] = useState("");
  const path = window.location.pathname;

  const selectLanguage = useCallback((nextLanguage: Language) => {
    setLanguage(nextLanguage);
    storeLanguage(nextLanguage);
    document.documentElement.lang = nextLanguage.toLowerCase();
  }, []);

  useEffect(() => {
    storeLanguage(language);
    document.documentElement.lang = language.toLowerCase();
  }, [language]);

  useEffect(() => {
    if (path !== "/callback") return;
    const code = new URLSearchParams(window.location.search).get("code");
    const error = new URLSearchParams(window.location.search).get("error_description");
    if (error) { setAuthError(error); return; }
    if (!code) { setAuthError("No authorization code was returned."); return; }
    finishLogin(config, code)
      .then((newSession) => { setSession(newSession); window.history.replaceState({}, "", "/staff"); window.location.reload(); })
      .catch((caught) => setAuthError(caught.message));
  }, [config, path]);

  if (path === "/staff") return <StaffPortal config={config} session={session} />;
  if (path === "/callback") return <main className="callback-screen"><img className="staff-login-logo" src="/church-logo-navy.png" alt="Redeemers Church of Christ — The Ark of Love" /><h1>{authError ? "Sign-in needs attention" : "Completing secure sign-in…"}</h1>{authError && <><p>{authError}</p><a className="button button-primary" href="/staff">Try again</a></>}</main>;

  const t = copy[language];
  const page = appText[language];
  return (
    <div className="public-site">
      {showLanguageWelcome && <div className="language-welcome-backdrop" role="presentation">
        <section className="language-welcome" role="dialog" aria-modal="true" aria-labelledby="language-welcome-title">
          <img className="church-logo" src="/church-logo-navy.png" alt="Redeemers Church of Christ — The Ark of Love" />
          <p className="eyebrow">English · Español · Français</p>
          <h2 id="language-welcome-title">{page.chooseLanguage}</h2>
          <p>{page.chooseLanguageHelp}</p>
          <div className="language-welcome-options">{(["EN", "ES", "FR"] as Language[]).map((item) => <button type="button" className={item === language ? "active" : ""} onClick={() => selectLanguage(item)} key={item}><strong>{languageNames[item]}</strong><small>{item}</small></button>)}</div>
          <button className="button button-primary wide" type="button" onClick={() => setShowLanguageWelcome(false)}>{page.continueIn} <ArrowRight size={18} /></button>
        </section>
      </div>}
      <header className="site-header">
        <a className="public-brand" href="#top"><img className="church-logo" src="/church-logo-navy.png" alt="Redeemers Church of Christ — The Ark of Love" /><div><strong>Redeemers</strong><small>Mega Health Fair</small></div></a>
        <nav><a href="#services">{page.navServices}</a><a href="#details">{page.navDetails}</a><a href="#register">{page.navRegistration}</a></nav>
        <div className="header-actions">
          <div className="language-picker" aria-label={page.chooseLanguage}><Languages size={17} />{(["EN", "ES", "FR"] as Language[]).map((item) => <button type="button" title={languageNames[item]} aria-pressed={item === language} className={item === language ? "active" : ""} onClick={() => selectLanguage(item)} key={item}>{item}</button>)}</div>
          <a className="button button-small button-secondary" href="/staff">{t.staff}</a>
        </div>
      </header>

      <main id="top">
        <section className="hero">
          <div className="hero-glow" />
          <div className="hero-copy">
            <p className="eyebrow"><Sparkles size={16} /> {t.eyebrow}</p>
            <h1>{t.title}</h1>
            <p className="hero-subtitle">{t.subtitle}</p>
            <div className="hero-actions"><a className="button button-primary" href="#register">{t.register} <ArrowRight size={18} /></a><a className="text-link" href="#details">{page.viewDetails}</a></div>
            <div className="trust-row"><span><Users size={19} /><strong>3,000–5,000</strong> {page.neighbors}</span><span><Stethoscope size={19} /><strong>16</strong> {page.healthServices}</span><span><Languages size={19} /><strong>EN · ES · FR</strong> {page.languageSupport}</span></div>
          </div>
          <div className="hero-visual" aria-label="Event highlights">
            <div className="visual-card main"><div className="pulse-mark"><HeartPulse size={42} /></div><p>{page.saturday}</p><strong>22</strong><span>{page.august}</span><hr /><small>10 AM—3 PM</small></div>
            <div className="visual-card floating one"><Activity size={24} /><span>{page.freeScreenings}</span></div>
            <div className="visual-card floating two"><School size={24} /><span>{page.schoolSupplies}</span></div>
            <div className="visual-card floating three"><ShoppingBasket size={24} /><span>{page.foodBags}</span></div>
          </div>
        </section>

        <section className="details-band" id="details">
          <article><CalendarDays /><div><small>{page.when}</small><strong>{t.date}</strong><span>{t.time}</span></div></article>
          <article><MapPin /><div><small>{page.where}</small><strong>10001 Aerospace Road</strong><span>Lanham, Maryland 20706</span></div></article>
          <article><HeartPulse /><div><small>{page.cost}</small><strong>{page.free}</strong><span>{page.encouraged}</span></div></article>
        </section>

        <section className="services-section" id="services">
          <div className="section-title"><p className="eyebrow">{page.careEyebrow}</p><h2>{page.careTitle}</h2><p>{page.careBody}</p></div>
          <div className="feature-grid">
            <article><span><Stethoscope /></span><h3>{page.screeningTitle}</h3><p>{page.screeningBody}</p><strong>{page.screeningMeta}</strong></article>
            <article><span><Activity /></span><h3>{page.wellnessTitle}</h3><p>{page.wellnessBody}</p><strong>{page.wellnessMeta}</strong></article>
            <article><span><ShoppingBasket /></span><h3>{page.familyTitle}</h3><p>{page.familyBody}</p><strong>{page.familyMeta}</strong></article>
          </div>
        </section>

        <Registration config={config} language={language} onLanguageChange={selectLanguage} />
      </main>
      <footer><a className="public-brand" href="#top"><img className="church-logo" src="/church-logo-white.png" alt="Redeemers Church of Christ — The Ark of Love" /><div><strong>Redeemers</strong><small>Mega Health Fair</small></div></a><p>{page.footerPrivacy}</p><a href="/staff">{page.footerStaff}</a></footer>
    </div>
  );
}
